import React, { useLayoutEffect, useMemo } from 'react';
import * as Stickyfill from 'stickyfilljs';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { selectAuditoriumByWebinarId } from 'models/auditoriums';
import { useParams } from 'react-router-dom';
import { AuditoriumAssetGroup, Auditorium } from 'models/auditoriums/types';
import BannerCarousel from 'pages/main_lobby/v2/BannerCarousel';
import findAvailableLocale from 'models/helpers/findAvailableLocale';
import { selectLocale } from 'models/localization';
import ScrollableContainerWrapper from 'components/ScrollableContainerWrapper';
import WebinarFloatingSlido from 'pages/webinar_detail/slido/WebinarFloatingSlido';
import { selectWebinarById } from 'models/webinars';

const isMSIE = navigator.userAgent.indexOf('MSIE') !== -1 || navigator.appVersion.indexOf('Trident/') > -1;

const NonAuditoriumRootContainer = styled.div`
  height: 100%;
  overflow: auto;
`;

const RootContainer = styled.div<{ backgroundImageSrc: string }>`
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100%;
  height: 100%;
  overflow: auto;

  @media (min-width: 960px) {
    background: url(${({ backgroundImageSrc }) => backgroundImageSrc}) no-repeat center center fixed;
    background-size: cover;
  }
`;

const PageWrapper = styled.div`
  flex: 1 0;
  max-width: 1280px;
  width: 100%;
  margin: auto;

  @media (min-width: 960px) {
    padding: 0 16px;
  }
`;

const AuditoriumBannerContainer = styled.div<{ hideOnMobile: boolean; useAbsolutePosition?: boolean }>`
  display: ${({ hideOnMobile }) => (hideOnMobile ? 'none' : 'flex')};
  flex-shrink: 0;
  width: 100%;
  height: 100%;
  position: ${({ useAbsolutePosition }) => (useAbsolutePosition ? 'absolute' : 'relative')};
  top: 50%;
  left: 50%;
  right: 0;
  bottom: 0;
  transform: translate(-50%, -50%);

  @media (min-width: 960px) {
    display: flex;
  }

  .slick-slide img {
    object-fit: cover;
  }
`;

const SliderContainer = styled.div`
  width: 100%;
  height: auto;
  padding: 0;
  max-height: initial;
`;

const AuditoriumContentContainer = styled.div`
  position: relative;
  display: block;
  overflow: auto;
  width: 100%;
  height: 100%;

  @media (min-width: 960px) {
    padding: 0 32px;
  }
`;

const AuditoriumBanner = ({ bannerData: { assets }, bannerPosition, hideOnMobile = false }: {
  hideOnMobile?: boolean;
  bannerData: AuditoriumAssetGroup;
  bannerPosition: string;
}) => {
  const containerId = `${bannerPosition}-auditorium-banner`;
  const useAbsolutePosition = bannerPosition === 'center';
  const isDesktop = useMediaQuery('(min-width: 960px)');
  const orderKey = useMemo(() => (isDesktop ? 'desktopOrder' : 'mobileOrder'), [isDesktop]);
  const sortedAssets = useMemo(() => (
    [...assets].sort((a, b) => a[orderKey] - b[orderKey])
  ), [assets, orderKey]);

  return (
    <AuditoriumBannerContainer useAbsolutePosition={useAbsolutePosition} hideOnMobile={hideOnMobile} id={containerId} className="auditorium-banner">
      <SliderContainer>
        <BannerCarousel banners={sortedAssets} maxSliderHeight={720} bullet />
      </SliderContainer>
    </AuditoriumBannerContainer>
  );
};

const TopBannerContainer = styled.div<{ percentage: number }>`
  padding-top: ${({ percentage }) => percentage}%;
  margin: 0 auto;
  position: relative;
  margin-bottom: 32px;
`;

const VerticalBannerContainer = styled.div<{
  percentage: number;
  hideOnMobile: boolean;
  appliedStickyPolyfill?: boolean;
}>`
  width: 180px;
  height: ${({ percentage }) => (180 * (percentage / 100))}px;
  position: sticky;
  align-self: flex-start;
  top: ${({ appliedStickyPolyfill }) => (appliedStickyPolyfill ? '80px' : '16px')};
  display: ${({ hideOnMobile }) => (hideOnMobile ? 'none' : 'flex')};

  @media (min-width: 960px) {
    display: flex;
  }
`;

const StickyVerticalBannerContainer = ({
  hideOnMobile,
  percentage,
  id,
  className,
  children,
}: any) => {
  useLayoutEffect(() => {
    if (!isMSIE) return () => null;

    const element = document.querySelector(`#${id}`);
    const container = document.querySelector('#auditorium-content-container');
    if (!element || !container) return () => null;

    const [sticky] = Stickyfill.add(element as any);
    let ticking = false;
    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          sticky.refresh();
          ticking = false;
        });

        ticking = true;
      }
    };
    container.addEventListener('scroll', onScroll);
    return () => {
      container.removeEventListener('scroll', onScroll);
      sticky.remove();
    };
  }, [id]);

  return (
    <VerticalBannerContainer
      id={id}
      hideOnMobile={hideOnMobile}
      percentage={percentage}
      className={className}
      appliedStickyPolyfill={isMSIE}
    >
      {children}
    </VerticalBannerContainer>
  );
};

const TopBannerWrapper = styled.div`
  width: 100%;
`;

const AuditoriumContent = styled.div`
  padding: 0 8px;
  justify-content: center;
  display: flex;

  @media (min-width: 960px) {
    padding: 0;
  }
`;

const AuditoriumWebinarDataContainer = styled.div`
  flex-direction: column;
  padding: 0 16px;
  flex: 1 0;
  ${isMSIE ? '& > div { display: ruby-base; }' : ''}
`;

const defaultBanner = {
  aspectX: 0,
  aspectY: 0,
  assets: [],
  sliderDuration: 5000,
};

const ContentContainer = styled(ScrollableContainerWrapper)`
  ${isMSIE ? 'overflow-x: hidden;' : ''}
`;

const AuditoriumWrapper = ({ children }: {
  children: React.ReactElement;
}) => {
  const { webinarId } = useParams() as { webinarId: string };
  const locale = useSelector(selectLocale);
  const { features } = useSelector((s) => s.eventConfigs);
  const hasAuditorium = features.includes('auditoriumWebinar');
  const auditorium = useSelector((state) => selectAuditoriumByWebinarId(state, webinarId));
  const webinar = useSelector((s) => selectWebinarById(s, webinarId));

  const leftAuditoriumBanner = auditorium?.assets?.left ?? defaultBanner;
  const centerAuditoriumBanner = auditorium?.assets?.center ?? defaultBanner;
  const rightAuditoriumBanner = auditorium?.assets?.right ?? defaultBanner;

  const resolvedAuditorium = auditorium || {} as Auditorium;

  const backgroundImageSrc = useMemo(() => findAvailableLocale(resolvedAuditorium.backgroundImage || {}, locale), [resolvedAuditorium.backgroundImage, locale]);

  const leftBannerPercentage = useMemo(() => (leftAuditoriumBanner.aspectY / leftAuditoriumBanner.aspectX) * 100, [leftAuditoriumBanner.aspectX, leftAuditoriumBanner.aspectY]);
  const centerBannerPercentage = useMemo(() => (centerAuditoriumBanner.aspectY / centerAuditoriumBanner.aspectX) * 100, [centerAuditoriumBanner.aspectX, centerAuditoriumBanner.aspectY]);
  const rightBannerPercentage = useMemo(() => (rightAuditoriumBanner.aspectY / rightAuditoriumBanner.aspectX) * 100, [rightAuditoriumBanner.aspectX, rightAuditoriumBanner.aspectY]);

  if (!hasAuditorium || !auditorium) {
    return (
      <ScrollableContainerWrapper WrapperContainer={NonAuditoriumRootContainer} id="non-auditorium-content-wrapper">
        {children}
      </ScrollableContainerWrapper>
    );
  }

  return (
    <RootContainer backgroundImageSrc={backgroundImageSrc} id="auditorium-root-container">
      <ContentContainer WrapperContainer={AuditoriumContentContainer} id="auditorium-content-container">
        <PageWrapper id="auditorium-page-wrapper">
          <TopBannerWrapper id="top-banner-wrapper">
            <TopBannerContainer percentage={centerBannerPercentage} id="top-auditorium-banner-container" className="auditorium-banner-container">
              <AuditoriumBanner bannerData={centerAuditoriumBanner} bannerPosition="center" />
            </TopBannerContainer>
          </TopBannerWrapper>
          <AuditoriumContent id="auditorium-content">
            {leftAuditoriumBanner.assets.length !== 0 && (
              <StickyVerticalBannerContainer hideOnMobile percentage={leftBannerPercentage} id="left-auditorium-banner-container" className="auditorium-banner-container">
                <AuditoriumBanner hideOnMobile bannerData={leftAuditoriumBanner} bannerPosition="left" />
              </StickyVerticalBannerContainer>
            )}
            <AuditoriumWebinarDataContainer id="auditorium-webinar-data-container">
              {children}
            </AuditoriumWebinarDataContainer>
            {rightAuditoriumBanner.assets.length !== 0 && (
              <StickyVerticalBannerContainer hideOnMobile percentage={rightBannerPercentage} id="right-auditorium-banner-container" className="auditorium-banner-container">
                <AuditoriumBanner hideOnMobile bannerData={rightAuditoriumBanner} bannerPosition="right" />
              </StickyVerticalBannerContainer>
            )}
          </AuditoriumContent>
        </PageWrapper>
        <WebinarFloatingSlido webinar={webinar} />
      </ContentContainer>
    </RootContainer>
  );
};

export default AuditoriumWrapper;
